import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import RoleBasedNavigation from '../../components/navigation/RoleBasedNavigation';
import Icon from '../../components/AppIcon';
import GlossaryTermCard from './components/GlossaryTermCard';

const StudentGlossaryExplorer = () => {
  const { userProfile } = useAuth();
  const [glossaryTerms, setGlossaryTerms] = useState([]);
  const [filteredTerms, setFilteredTerms] = useState([]);
  const [plays, setPlays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPlay, setSelectedPlay] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedDifficulty, setSelectedDifficulty] = useState('all');
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchGlossaryData();
  }, []);

  useEffect(() => {
    filterTerms();
  }, [searchQuery, selectedPlay, selectedCategory, selectedDifficulty, glossaryTerms]);

  const fetchGlossaryData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch all glossary terms with play information
      const { data: termsData, error: termsError } = await supabase?.from('glossary_terms')?.select(`
          *,
          plays (
            id,
            title
          )
        `)?.order('term', { ascending: true });

      if (termsError) throw termsError;

      // Fetch all published plays for the filter dropdown
      const { data: playsData, error: playsError } = await supabase?.from('plays')?.select('id, title')?.eq('is_published', true)?.order('title', { ascending: true });

      if (playsError) throw playsError;

      setGlossaryTerms(termsData || []);
      setPlays(playsData || []);
    } catch (err) {
      console.error('Error fetching glossary data:', err);
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const filterTerms = () => {
    let filtered = [...glossaryTerms];

    // Filter by search query
    if (searchQuery) {
      filtered = filtered?.filter(
        (term) =>
          term?.term?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
          term?.definition?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
          term?.example_usage?.toLowerCase()?.includes(searchQuery?.toLowerCase())
      );
    }

    // Filter by play
    if (selectedPlay !== 'all') {
      filtered = filtered?.filter((term) => term?.play_id === selectedPlay);
    }

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered?.filter((term) => term?.category === selectedCategory);
    }

    // Filter by difficulty
    if (selectedDifficulty !== 'all') {
      filtered = filtered?.filter((term) => term?.difficulty === selectedDifficulty);
    }

    setFilteredTerms(filtered);
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'vocabulary':
        return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'character':
        return 'bg-purple-500/20 text-purple-400 border-purple-500/30';
      case 'theme':
        return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'literary_device':
        return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      default:
        return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'easy':
        return 'text-green-400';
      case 'medium':
        return 'text-yellow-400';
      case 'hard':
        return 'text-red-400';
      default:
        return 'text-gray-400';
    }
  };

  // Get unique categories from glossary terms
  const categories = [...new Set(glossaryTerms?.map(term => term?.category)?.filter(Boolean))];

  return (
    <>
      <Helmet>
        <title>Glossary Explorer - BardPlay</title>
        <meta 
          name="description" 
          content="Search and explore Shakespeare's vocabulary, characters, themes, and literary devices with BardPlay's interactive glossary" 
        />
      </Helmet>
      <RoleBasedNavigation />
      <main className="min-h-screen bg-gradient-to-br from-surface-dark via-[#1a0033] to-surface-dark pt-20 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header Section */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <Icon name="BookText" size={40} className="text-accent-purple" />
              <h1 className="text-4xl font-bold bg-gradient-to-r from-accent-purple to-accent-blue bg-clip-text text-transparent">
                Shakespeare's Glossary
              </h1>
            </div>
            <p className="text-text-secondary text-lg">
              Explore and search through vocabulary, characters, themes, and literary devices from Shakespeare's plays
            </p>
          </div>

          {/* Search and Filter Bar */}
          <div className="bg-surface-lighter border border-border rounded-xl p-6 mb-8">
            {/* Search Input */}
            <div className="relative mb-4">
              <Icon 
                name="Search" 
                size={20} 
                className="absolute left-4 top-1/2 -translate-y-1/2 text-text-secondary" 
              />
              <input
                type="text"
                placeholder="Search terms, definitions, or examples..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e?.target?.value)}
                className="w-full pl-12 pr-4 py-3 bg-surface-dark border border-border rounded-lg text-white placeholder-text-secondary focus:outline-none focus:border-accent-purple transition-colors"
              />
            </div>

            {/* Filter Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Play Filter */}
              <div className="relative">
                <Icon 
                  name="Theater" 
                  size={20} 
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-text-secondary" 
                />
                <select
                  value={selectedPlay}
                  onChange={(e) => setSelectedPlay(e?.target?.value)}
                  className="w-full pl-12 pr-4 py-3 bg-surface-dark border border-border rounded-lg text-white focus:outline-none focus:border-accent-purple transition-colors appearance-none cursor-pointer"
                >
                  <option value="all">All Plays</option>
                  {plays?.map((play) => (
                    <option key={play?.id} value={play?.id}>
                      {play?.title}
                    </option>
                  ))}
                </select>
                <Icon 
                  name="ChevronDown" 
                  size={20} 
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-text-secondary pointer-events-none" 
                />
              </div>

              {/* Category Filter */}
              <div className="relative">
                <Icon 
                  name="Tag" 
                  size={20} 
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-text-secondary" 
                />
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e?.target?.value)}
                  className="w-full pl-12 pr-4 py-3 bg-surface-dark border border-border rounded-lg text-white focus:outline-none focus:border-accent-purple transition-colors appearance-none cursor-pointer"
                >
                  <option value="all">All Categories</option>
                  {categories?.map((category) => (
                    <option key={category} value={category}>
                      {category?.replace('_', ' ')?.replace(/\b\w/g, l => l?.toUpperCase())}
                    </option>
                  ))}
                </select>
                <Icon 
                  name="ChevronDown" 
                  size={20} 
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-text-secondary pointer-events-none" 
                />
              </div>

              {/* Difficulty Filter */}
              <div className="relative">
                <Icon 
                  name="Filter" 
                  size={20} 
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-text-secondary" 
                />
                <select
                  value={selectedDifficulty}
                  onChange={(e) => setSelectedDifficulty(e?.target?.value)}
                  className="w-full pl-12 pr-4 py-3 bg-surface-dark border border-border rounded-lg text-white focus:outline-none focus:border-accent-purple transition-colors appearance-none cursor-pointer"
                >
                  <option value="all">All Difficulties</option>
                  <option value="easy">Easy</option>
                  <option value="medium">Medium</option>
                  <option value="hard">Hard</option>
                </select>
                <Icon 
                  name="ChevronDown" 
                  size={20} 
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-text-secondary pointer-events-none" 
                />
              </div>
            </div>

            {/* Active Filters Display */}
            {(searchQuery || selectedPlay !== 'all' || selectedCategory !== 'all' || selectedDifficulty !== 'all') && (
              <div className="flex flex-wrap items-center gap-2 mt-4 pt-4 border-t border-border">
                <span className="text-sm text-text-secondary">Active filters:</span>
                {searchQuery && (
                  <span className="px-3 py-1 bg-accent-purple/20 text-accent-purple rounded-full text-sm flex items-center gap-2">
                    Search: "{searchQuery}"
                    <button onClick={() => setSearchQuery('')} className="hover:text-white">
                      <Icon name="X" size={14} />
                    </button>
                  </span>
                )}
                {selectedPlay !== 'all' && (
                  <span className="px-3 py-1 bg-accent-purple/20 text-accent-purple rounded-full text-sm flex items-center gap-2">
                    {plays?.find(p => p?.id === selectedPlay)?.title}
                    <button onClick={() => setSelectedPlay('all')} className="hover:text-white">
                      <Icon name="X" size={14} />
                    </button>
                  </span>
                )}
                {selectedCategory !== 'all' && (
                  <span className="px-3 py-1 bg-accent-purple/20 text-accent-purple rounded-full text-sm flex items-center gap-2 capitalize">
                    {selectedCategory?.replace('_', ' ')}
                    <button onClick={() => setSelectedCategory('all')} className="hover:text-white">
                      <Icon name="X" size={14} />
                    </button>
                  </span>
                )}
                {selectedDifficulty !== 'all' && (
                  <span className="px-3 py-1 bg-accent-purple/20 text-accent-purple rounded-full text-sm flex items-center gap-2 capitalize">
                    {selectedDifficulty}
                    <button onClick={() => setSelectedDifficulty('all')} className="hover:text-white">
                      <Icon name="X" size={14} />
                    </button>
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Error Display */}
          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-8 flex items-start gap-3">
              <Icon name="AlertCircle" size={24} className="text-red-400 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-red-400 font-semibold mb-1">Error Loading Glossary</h3>
                <p className="text-red-300/80 text-sm">{error}</p>
              </div>
            </div>
          )}

          {/* Loading State */}
          {loading && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[...Array(8)]?.map((_, index) => (
                <div 
                  key={index} 
                  className="bg-surface-lighter border border-border rounded-xl h-48 animate-pulse"
                />
              ))}
            </div>
          )}

          {/* Glossary Terms Grid */}
          {!loading && !error && (
            <>
              {filteredTerms?.length === 0 ? (
                <div className="text-center py-16">
                  <Icon name="BookText" size={64} className="text-text-secondary mx-auto mb-4 opacity-50" />
                  <h3 className="text-xl font-semibold text-white mb-2">No Terms Found</h3>
                  <p className="text-text-secondary mb-6">
                    {searchQuery || selectedPlay !== 'all' || selectedCategory !== 'all' || selectedDifficulty !== 'all'
                      ? 'Try adjusting your filters to see more terms' :'No glossary terms are currently available'}
                  </p>
                  {(searchQuery || selectedPlay !== 'all' || selectedCategory !== 'all' || selectedDifficulty !== 'all') && (
                    <button
                      onClick={() => {
                        setSearchQuery('');
                        setSelectedPlay('all');
                        setSelectedCategory('all');
                        setSelectedDifficulty('all');
                      }}
                      className="btn-secondary"
                    >
                      Clear All Filters
                    </button>
                  )}
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between mb-4">
                    <p className="text-text-secondary">
                      Showing <span className="text-white font-semibold">{filteredTerms?.length}</span> term{filteredTerms?.length !== 1 ? 's' : ''}
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {filteredTerms?.map((term) => (
                      <GlossaryTermCard
                        key={term?.id}
                        term={term}
                        getCategoryColor={getCategoryColor}
                        getDifficultyColor={getDifficultyColor}
                      />
                    ))}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </main>
    </>
  );
};

export default StudentGlossaryExplorer;